import { create } from 'zustand';
import type {
  AuthStatePayload,
  AuthSubscriptionPayload,
} from '@claudafinil/shared/contract';
import type { UserInfo } from '../../lib/api';

export interface AuthSessionState {
  isAuthenticated: boolean;
  user: Record<string, unknown> | null;
  subscription: AuthSubscriptionPayload | null;
  accessToken: string | null;
}

export interface AuthSessionActions {
  setAuthState: (payload: AuthStatePayload) => void;
  mergeUserInfo: (userInfo: UserInfo) => void;
  setAccessToken: (token: string | null) => void;
  reset: () => void;
}

export type AuthSessionStore = AuthSessionState & AuthSessionActions;

const initialState: AuthSessionState = {
  isAuthenticated: false,
  user: null,
  subscription: null,
  accessToken: null,
};

function toUserRecord(user: AuthStatePayload['user']): Record<string, unknown> | null {
  if (!user || typeof user !== 'object') {
    return null;
  }

  return user as Record<string, unknown>;
}

function toSubscription(userInfo: UserInfo): AuthSubscriptionPayload {
  return {
    isPro: userInfo.isPro,
    status: null,
    trialEndsAt: null,
    currentPeriodEnd: null,
  };
}

export const useAuthSessionStore = create<AuthSessionStore>((set) => ({
  ...initialState,

  setAuthState: (payload) => {
    set({
      isAuthenticated: payload.isAuthenticated,
      user: toUserRecord(payload.user),
      subscription: payload.subscription,
    });
  },

  mergeUserInfo: (userInfo) => {
    set((state) => ({
      isAuthenticated: true,
      user: {
        ...(state.user ?? {}),
        ...userInfo,
      },
      subscription: toSubscription(userInfo),
    }));
  },

  setAccessToken: (token) => {
    set({
      accessToken: token && token.length > 0 ? token : null,
    });
  },

  reset: () => {
    set(initialState);
  },
}));
